/**
 * Formats first and last names into a single full name string
 * @param firstName - The first name
 * @param lastName - The last name
 * @returns Formatted full name string
 */
export function formatFullName(firstName: string, lastName: string): string {
  return `${firstName.trim()} ${lastName.trim()}`.trim();
}

/**
 * Generates all possible name variations from primary and alternate names
 * @param params - Object containing primary and alternate names
 * @returns Comma-separated string of all name variations
 */
export function generateNameVariations(params: {
  primaryFirstName: string;
  primaryLastName: string;
  firstNameAlternates: string[];
  lastNameAlternates: string[];
}): string {
  const {
    primaryFirstName,
    primaryLastName,
    firstNameAlternates,
    lastNameAlternates,
  } = params;

  if (!firstNameAlternates.length && !lastNameAlternates.length) {
    return '';
  }

  const combinations = new Set<string>();

  // Generate all combinations of first name and last name alternate values
  firstNameAlternates.forEach((firstName: string) => {
    lastNameAlternates.forEach((lastName: string) => {
      combinations.add(`${firstName} ${lastName}`);
    });
  });

  // Add combinations with primary first name + alternate last names (skip primary last)
  firstNameAlternates.forEach((firstName: string) => {
    if (firstName !== primaryFirstName && primaryLastName) {
      combinations.add(`${firstName} ${primaryLastName}`);
    }
  });

  // Add combinations with alternate first names + primary last name (skip primary first)
  lastNameAlternates.forEach((lastName: string) => {
    if (lastName !== primaryLastName && primaryFirstName) {
      combinations.add(`${primaryFirstName} ${lastName}`);
    }
  });

  return Array.from(combinations).join(', ');
}

export const capitalize = (name: string): string =>
  name.charAt(0).toUpperCase() + name.slice(1);